import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "../lib/api";

// ── Cache configuration ──────────────────────────────────────────
export const PROPERTY_STALE_TIME = 24 * 60 * 60 * 1000; // 24 hours
export const PROPERTY_GC_TIME = 60 * 60 * 1000; // 1 hour

// ── Query key factory ────────────────────────────────────────────
export const propertyKeys = {
  all: ["properties"] as const,
  enriched: ["properties", "enriched"] as const,
  stats: ["properties", "stats"] as const,
  neighborhoods: ["properties", "neighborhoods"] as const,
  managementCompanies: ["properties", "management-companies"] as const,
  searchNeighborhoods: ["properties", "search-neighborhoods"] as const,
  search: (params: Record<string, unknown>) =>
    ["properties", "search", params] as const,
  floorPlans: (propertyId: string) =>
    ["properties", "floor-plans", propertyId] as const,
  units: (propertyId: string) =>
    ["properties", "units", propertyId] as const,
};

const queryConfig = {
  staleTime: PROPERTY_STALE_TIME,
  gcTime: PROPERTY_GC_TIME,
};

// ── Hooks ────────────────────────────────────────────────────────

export function usePropertiesEnriched() {
  return useQuery({
    queryKey: propertyKeys.enriched,
    queryFn: async () => {
      const data = await fetchWithAuth<any[]>("/properties-management/enriched", {
        // Full production inventory enrichment can exceed the default 30s timeout.
        timeoutMs: 90000,
      });
      return data.map((p: any) => ({
        ...p,
        phone: p.phone ?? p.contact_phone ?? null,
        property_website: p.property_website ?? p.website ?? null,
        pet_policy_details: p.pet_policy_details ?? p.pet_policy ?? null,
        pet_deposit: p.pet_deposit ?? p.pet_fee ?? null,
        is_favorite: p.is_favorite ?? false,
      }));
    },
    ...queryConfig,
  });
}

export function usePropertyStats() {
  return useQuery({
    queryKey: propertyKeys.stats,
    queryFn: () => fetchWithAuth<any>("/properties-management/stats"),
    ...queryConfig,
  });
}

export function useNeighborhoods() {
  return useQuery({
    queryKey: propertyKeys.neighborhoods,
    queryFn: () => fetchWithAuth<string[]>("/properties-management/neighborhoods"),
    ...queryConfig,
  });
}

export function useManagementCompanies() {
  return useQuery({
    queryKey: propertyKeys.managementCompanies,
    queryFn: () => fetchWithAuth<string[]>("/properties-management/management-companies"),
    ...queryConfig,
  });
}

export function useSearchNeighborhoods() {
  return useQuery({
    queryKey: propertyKeys.searchNeighborhoods,
    queryFn: async () => {
      const data = await fetchWithAuth<any[]>("/analytics/neighborhood-analysis");
      return data
        .map((item: any) => item.neighborhood)
        .filter((n: string | null) => n != null && n.trim() !== "");
    },
    ...queryConfig,
  });
}

// ── Invalidation helper ──────────────────────────────────────────

export function usePropertyInvalidation() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () =>
      queryClient.invalidateQueries({ queryKey: propertyKeys.all }),

    invalidateProperty: (propertyId: string) => {
      queryClient.invalidateQueries({ queryKey: propertyKeys.floorPlans(propertyId) });
      queryClient.invalidateQueries({ queryKey: propertyKeys.units(propertyId) });
      queryClient.invalidateQueries({ queryKey: propertyKeys.enriched });
      queryClient.invalidateQueries({ queryKey: propertyKeys.stats });
    },

    invalidateSearch: () =>
      queryClient.invalidateQueries({
        queryKey: ["properties", "search"],
      }),
  };
}
